import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { Calendar, Clock, MapPin, ShieldCheck, QrCode, UserCheck, Plus, Sparkles } from "lucide-react";
import VolunteerCheckInModal from "@/components/VolunteerCheckInModal";
import { toast } from "sonner";

export default function VolunteerSignupPage() {
  const queryClient = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [registeredShift, setRegisteredShift] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    waiver_signed: false,
    waiver_signature: "",
  });

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: async () => {
      const res = await apiClient.get("/events");
      return res.data;
    },
  });

  const { data: shifts = [], refetch: refetchShifts } = useQuery({
    queryKey: ["shifts", selectedEvent?._id],
    queryFn: async () => {
      if (!selectedEvent) return [];
      const res = await apiClient.get(`/events/${selectedEvent._id}/shifts`);
      return res.data;
    },
    enabled: !!selectedEvent,
  });

  const registerShiftMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post(`/events/${selectedEvent._id}/shifts`, data);
      return res.data;
    },
    onSuccess: (data) => {
      setRegisteredShift(data);
      refetchShifts();
      toast.success("Shift registered and digital waiver recorded!");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to register for shift");
    },
  });

  const createEventMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/events", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["events"]);
      toast.success("New volunteer event scheduled!");
    },
  });

  const handleSeedDemoEvent = () => {
    createEventMutation.mutate({
      title: "Community Food Drive & Distribution",
      description: "Assist with sorting, packing, and handing out emergency groceries to local families.",
      location: "Project Miracle Central Warehouse, Community Center",
      start_time: new Date(Date.now() + 86400000).toISOString(),
      end_time: new Date(Date.now() + 100800000).toISOString(),
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserCheck className="w-7 h-7 text-indigo-600" />
            Volunteer Intake, Waivers & QR Check-in
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Schedule shifts, execute digital liability waivers, and manage on-site QR attendance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSeedDemoEvent}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs rounded-lg transition-colors inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-4 h-4 text-amber-500" />
            Create Demo Event
          </button>
          <button
            disabled={!selectedEvent}
            onClick={() => setIsCheckInOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium text-xs rounded-lg shadow-sm transition-all"
          >
            <QrCode className="w-4 h-4" />
            Launch On-Site Check-in Scanner
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Events Directory */}
        <div className="space-y-4">
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
            Active Volunteer Events
          </h2>
          {isLoading ? (
            <div className="p-6 bg-white rounded-xl border border-slate-200 text-xs text-slate-500">
              Loading events...
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 bg-white rounded-xl border border-slate-200 text-center space-y-3">
              <Calendar className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-xs text-slate-500">No scheduled events found.</p>
              <button
                onClick={handleSeedDemoEvent}
                className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg"
              >
                Seed Miracle Food Drive
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((evt) => (
                <div
                  key={evt._id}
                  onClick={() => {
                    setSelectedEvent(evt);
                    setRegisteredShift(null);
                  }}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    selectedEvent?._id === evt._id
                      ? "bg-indigo-50/60 border-indigo-600 ring-2 ring-indigo-500/20"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <h3 className="font-semibold text-slate-900 text-sm">{evt.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{evt.description}</p>
                  <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      {evt.location}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Registration & Digital Waiver Form */}
        <div className="lg:col-span-2 space-y-6">
          {selectedEvent ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
              <div>
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                  Shift Registration & Digital Waiver
                </span>
                <h2 className="text-xl font-bold text-slate-900 mt-1">{selectedEvent.title}</h2>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {selectedEvent.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    4.0 Hours Credit
                  </span>
                </div>
              </div>

              {registeredShift ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-emerald-900">Shift Confirmed!</h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Digital waiver successfully recorded for {registeredShift.volunteer_name}.
                    </p>
                  </div>

                  {/* Display QR Token for Check in */}
                  <div className="bg-white p-4 rounded-xl border border-emerald-200 inline-block space-y-2">
                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">
                      Your On-Site QR Check-In Token
                    </span>
                    <div className="font-mono text-sm font-bold text-indigo-700 bg-slate-100 py-2 px-4 rounded-lg">
                      {registeredShift.qr_token}
                    </div>
                    <p className="text-[10px] text-slate-400">
                      Show this code to team leads at check-in desk.
                    </p>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!form.waiver_signed) {
                      toast.error("You must accept the digital waiver to proceed.");
                      return;
                    }
                    registerShiftMutation.mutate(form);
                  }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-700">First Name</label>
                      <input
                        required
                        type="text"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700">Last Name</label>
                      <input
                        required
                        type="text"
                        value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-700">Email Address</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700">Phone Number</label>
                      <input
                        type="text"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                      />
                    </div>
                  </div>

                  {/* Digital Waiver Box */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-indigo-600" />
                      Digital Liability Waiver & Consent
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed bg-white p-3 rounded-lg border border-slate-200/80">
                      {selectedEvent.waiver_text ||
                        "I hereby release and hold harmless Project Miracle and Trust Dime from any liability during volunteer participation."}
                    </p>

                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="checkbox"
                        id="waiver_signed"
                        checked={form.waiver_signed}
                        onChange={(e) => setForm({ ...form, waiver_signed: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300"
                      />
                      <label htmlFor="waiver_signed" className="text-xs font-medium text-slate-700">
                        I read, understand, and agree to the digital waiver terms.
                      </label>
                    </div>

                    {form.waiver_signed && (
                      <div>
                        <label className="text-[11px] font-medium text-slate-600">
                          Digital Signature (Type Full Legal Name)
                        </label>
                        <input
                          required
                          type="text"
                          placeholder="e.g. Jane Doe"
                          value={form.waiver_signature}
                          onChange={(e) => setForm({ ...form, waiver_signature: e.target.value })}
                          className="w-full mt-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-serif italic"
                        />
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={registerShiftMutation.isPending}
                    className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-sm transition-colors"
                  >
                    {registerShiftMutation.isPending
                      ? "Registering Shift..."
                      : "Confirm Shift Registration & Sign Waiver"}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center text-sm text-slate-400">
              Select an event from the left list to register for a shift.
            </div>
          )}
        </div>
      </div>

      <VolunteerCheckInModal
        isOpen={isCheckInOpen}
        onClose={() => setIsCheckInOpen(false)}
        shifts={shifts}
        onRefresh={refetchShifts}
      />
    </div>
  );
}
